import { Box, Button, Divider, Typography } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { CategoryConfiguration } from "../../Create/components";
import { Action } from "./Action";
import newProductCategoryResolver from "../../../../../libs/form-validations/new-product-category";
import type { IProductCategory } from "../../../../../interfaces";

interface CategoryDetailsProps {
    category: IProductCategory;
    onUpdate: (data: Partial<IProductCategory>) => Promise<void>;
    onDelete: () => Promise<void>;
    isUpdating: boolean;
    isDeleting: boolean;
}

export function CategoryDetails({ category, onUpdate, onDelete, isUpdating, isDeleting }: CategoryDetailsProps) {
    const navigate = useNavigate();

    const methods = useForm({
        resolver: newProductCategoryResolver,
        defaultValues: {
            Name: category.Name || "",
            Description: category.Description || "",
            DefaultUnitType: category.DefaultUnitType || "",
            MeasurementTypes: category.MeasurementTypes || null,
            IsActive: typeof category.IsActive === "number" ? category.IsActive === 1 : category.IsActive ?? true,
        },
    });

    const onSubmit = methods.handleSubmit(async (data) => {
        await onUpdate({
            Name: data.Name,
            Description: data.Description || undefined,
            DefaultUnitType: data.DefaultUnitType || undefined,
            MeasurementTypes: data.MeasurementTypes || undefined,
            IsActive: data.IsActive ?? true,
        });
    });

    return (
        <Box component="div" width="100%" p={2} overflow="auto">
            <FormProvider {...methods}>
                <form onSubmit={onSubmit}>
                    <Box component="div" display="flex" flexDirection="column" gap={3} maxWidth={800}>
                        <Box>
                            <Typography level="title-sm">Product Category Details</Typography>
                            <Typography level="body-sm">Update the product category information below.</Typography>
                        </Box>

                        <CategoryConfiguration />

                        <Box display="flex" gap={2} justifyContent="flex-start" mt={2}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                onClick={() => navigate("/admin/product-category/list")}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isUpdating || !methods.formState.isDirty}
                                loading={isUpdating}
                                loadingPosition="start"
                                variant="soft"
                                color="primary"
                            >
                                Update Category
                            </Button>
                        </Box>

                        <Divider />

                        <Action onDelete={onDelete} isDeleting={isDeleting} categoryName={category.Name} />
                    </Box>
                </form>
            </FormProvider>
        </Box>
    );
}
